import React, { useEffect } from "react";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getPerm } from "../../../utils/perm";
import { toast } from "react-toastify";

import {
  clearBackupMsg,
  fetchBackupListThunk,
  takeBackupThunk,
  downloadBackupThunk,
} from "../../../store/slices/backupDatabaseSlice";

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
};

export default function BackupDatabasePage() {
  const dispatch = useDispatch();

  const {
    list = [],
    loading = false,
    takingBackup = false,
    downloading = null,
    error = "",
    success = "",
  } = useSelector((s) => s.backupDatabase || {});

  const permsMap = useSelector((s) => s.permissions?.map) || {};
  const perms = getPerm(permsMap, "backup_database") || {};
  const canView = !!perms.can_view;
  const canUpdate = !!perms.can_update;

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearBackupMsg());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearBackupMsg());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (canView) {
      dispatch(fetchBackupListThunk());
    }
  }, [dispatch, canView]);

 const handleTakeBackup = () => {
  if (!canUpdate) return;

  toast.info(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Confirm Backup
        </div>
        <div style={{ fontSize: 14, marginBottom: 12 }}>
          Are you sure you want to take database backup now?
        </div>

        <div className="d-flex gap-2 justify-content-end">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => closeToast?.()}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              closeToast?.();
              dispatch(takeBackupThunk());
            }}
          >
            Yes, Take Backup
          </Button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    }
  );
};
  const handleRefresh = () => {
    if (!canView) return;
    dispatch(fetchBackupListThunk());
  };

  const handleDownload = (fileName) => {
    if (!fileName) return;
    dispatch(downloadBackupThunk(fileName));
  };

  return (
    <div className="container-fluid">
      <Row className="mb-3">
        <Col>
          <h4 style={{ fontWeight: 550, marginBottom: 4 }}>Backup Database</h4>
          <div style={{ color: "#050505", fontSize: 15 }}>
            Take Backup and download latest database backup files
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={12}>
          <Card style={{ borderRadius: 14 }}>
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                  <div style={{ fontWeight: 500, fontSize: 18 }}>
                    Database Backup Management
                  </div>
                  {/* <div style={{ color: "#6b7280", fontSize: 13 }}>
                    Only latest 3 backup files will be kept automatically
                  </div> */}
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleRefresh}
                    disabled={!canView || loading}
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleTakeBackup}
                    disabled={!canUpdate || takingBackup}
                  >
                    {takingBackup ? "Taking Backup..." : "Take Backup"}
                  </Button>
                </div>
              </div>

              {!canView ? (
                <div style={{ color: "#ef4444", fontWeight: 500 }}>
                  No View permission
                </div>
              ) : loading ? (
                <div style={{ padding: 20, textAlign: "center" }}>
                  <Spinner animation="border" />
                  <div style={{ marginTop: 10, color: "#6b7280" }}>
                    Loading backup files...
                  </div>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <Table hover responsive className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 70 }}>#</th>
                        <th>File Name</th>
                        <th style={{ minWidth: 140 }}>Size</th>
                        <th style={{ minWidth: 200 }}>Created At</th>
                        <th style={{ minWidth: 140, textAlign: "center" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(list) && list.length > 0 ? (
                        list.map((row, index) => (
                          <tr key={row.file_name || index}>
                            <td>{index + 1}</td>
                            <td style={{ fontWeight: 500 }}>{row.file_name || "-"}</td>
                            <td>{formatBytes(row.size)}</td>
                            <td>{formatDateTime(row.created_at)}</td>
                            <td style={{ textAlign: "center" }}>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={downloading === row.file_name}
                                onClick={() => handleDownload(row.file_name)}
                              >
                                {downloading === row.file_name
                                  ? "Downloading..."
                                  : "Download"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-muted">
                            No backup files available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {!canUpdate ? (
                <div style={{ marginTop: 12, color: "#ef4444", fontWeight: 500 }}>
                  No Update permission
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}